// Contains the room state and all the functions to manipulate it
import { GameElementType, SenderRoleType, } from '../types';
import { randomItem } from '../utilities/randomSelection';
import { hasContent } from '../utilities/hasContent';
/**
 * Creates an empty room state with required initial values
 */
export function createEmptyRoomState(roomId, roomCode, host, gameConfig) {
    return {
        roomId: roomId,
        roomCode,
        lastEventVersion: 0,
        status: 'opened',
        host: {
            userId: host.userId,
            userPreferences: host.userPreferences,
            modSettings: host.modSettings,
        },
        gameConfig,
        players: {},
        teams: {},
        turns: {},
        prompts: {},
        answers: {},
        votes: {},
        currentInteractionWindow: undefined,
        windowWaitingFor: [],
        turnsRemaining: 0,
        infoElements: {},
        mediaElements: {},
        scoreElements: {},
        playerScoreboard: '',
        playerLeaderboard: '',
        teamScoreboard: '',
        teamLeaderboard: '',
    };
}
/**
 * Creates a room state from a JSON snapshot
 * Validates that the ID is a valid MongoDB ObjectId
 */
export function createRoomStateFromSnapshot(snapshot) {
    // Validate required fields
    const requiredFields = [
        'roomId',
        'roomCode',
        'host',
        'gameConfig',
        'lastEventVersion',
        'players',
        'teams',
        'turns',
        'prompts',
        'answers',
        'votes',
        'infoElements',
        'mediaElements',
        'scoreElements',
    ];
    for (const field of requiredFields) {
        if (!(field in snapshot)) {
            throw new Error(`Missing required field: ${field}`);
        }
    }
    return snapshot;
}
/**
 * Serialize RoomState to JSON string
 */
export function serializeRoomState(state) {
    return JSON.stringify(state);
}
/**
 * Parse JSON string back to RoomState object
 */
export function deserializeRoomState(json) {
    try {
        const parsed = JSON.parse(json);
        return createRoomStateFromSnapshot(parsed);
    }
    catch (error) {
        throw new Error(`Failed to deserialize room state: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
}
/**
 * Gets a player by their ID
 */
export function getPlayerById(state, playerId) {
    return state.players[playerId];
}
/**
 * Gets the player assigned to any game element (turn, prompt, answer)
 */
export function getAssignedPlayer(state, elementId, elementType) {
    let element;
    switch (elementType) {
        case GameElementType.TURN:
            element = state.turns[elementId];
            break;
        case GameElementType.PROMPT:
            element = state.prompts[elementId];
            break;
        case GameElementType.ANSWER:
            element = state.answers[elementId];
            break;
        default:
            return undefined;
    }
    if (!element?.assignedPlayerId)
        return undefined;
    return state.players[element.assignedPlayerId];
}
/**
 * Gets the team assigned to any game element
 */
export function getAssignedTeam(state, elementId, elementType) {
    let element;
    switch (elementType) {
        case GameElementType.TURN:
            element = state.turns[elementId];
            break;
        case GameElementType.PROMPT:
            element = state.prompts[elementId];
            break;
        case GameElementType.ANSWER:
            element = state.answers[elementId];
            break;
        default:
            return undefined;
    }
    if (!element?.assignedTeamId)
        return undefined;
    return state.teams[element.assignedTeamId];
}
/**
 * Gets the active interaction window if one exists
 */
export function getActiveInteractionWindow(state) {
    return state.currentInteractionWindow?.windowStatus === 'opened'
        ? state.currentInteractionWindow
        : undefined;
}
/**
 * Gets the total number of players in the room
 */
export function getPlayerCount(state) {
    return getPlayerIds(state).length;
}
/**
 * Gets whether minimum player count is reached
 */
export function getMinPlayersReached(state) {
    return getPlayerCount(state) >= state.gameConfig.gamePlayersMin;
}
/**
 * Gets whether maximum player count is reached
 */
export function getMaxPlayersReached(state) {
    return getPlayerCount(state) >= state.gameConfig.gamePlayersMax;
}
/**
 * Gets remaining room capacity
 */
export function getRoomCapacityRemaining(state) {
    return Math.max(0, state.gameConfig.gamePlayersMax - getPlayerCount(state));
}
/**
 * Getter for room status
 */
export function getRoomStatus(state) {
    return state.status;
}
/**
 * Returns null to represent a destroyed room state.
 */
export function destroyRoomState() {
    return null;
}
/**
 * Gets the non-eliminated playerId with the least assignments in a given collection (turns or prompts)
 * If tied, returns a random playerId among those with the minimum count
 */
export function getPlayerWithLeastAssignments(state, collection) {
    // Get only non-eliminated players
    const playerIds = getPlayersNotEliminated(state).map((p) => p.playerId);
    if (playerIds.length === 0) {
        throw new Error('No non-eliminated players in the room');
    }
    // Initialize assignment counts for all non-eliminated players
    const assignmentCounts = Object.fromEntries(playerIds.map((id) => [id, 0]));
    // Count assignments in the specified collection
    for (const element of Object.values(state[collection])) {
        const assignedPlayerId = element
            .assignedPlayerId;
        if (assignedPlayerId &&
            Object.prototype.hasOwnProperty.call(assignmentCounts, assignedPlayerId)) {
            assignmentCounts[assignedPlayerId]++;
        }
    }
    // Find the minimum assignment count
    const minCount = Math.min(...Object.values(assignmentCounts));
    // Find all playerIds with the minimum assignment count
    const tiedPlayerIds = playerIds.filter((id) => assignmentCounts[id] === minCount);
    // Randomly select one of the tied playerIds
    const chosenId = randomItem(tiedPlayerIds);
    const player = getPlayerById(state, chosenId);
    if (!player) {
        throw new Error('Player not found');
    }
    return player;
}
/**
 * Gets the next non-eliminated player in room order, wrapping after the end.
 * If previousPlayerId is omitted or not found, returns the first non-eliminated
 * player in room order.
 */
export function getNextPlayerInOrder(state, previousPlayerId) {
    const orderedPlayers = getPlayers(state);
    if (orderedPlayers.length === 0) {
        throw new Error('No players in the room');
    }
    const activePlayerCount = orderedPlayers.filter((player) => !player.isEliminated).length;
    if (activePlayerCount === 0) {
        throw new Error('No non-eliminated players in the room');
    }
    const previousPlayerIndex = previousPlayerId
        ? orderedPlayers.findIndex((player) => player.playerId === previousPlayerId)
        : -1;
    const startIndex = previousPlayerIndex >= 0 ? previousPlayerIndex + 1 : 0;
    for (let offset = 0; offset < orderedPlayers.length; offset++) {
        const candidate = orderedPlayers[(startIndex + offset) % orderedPlayers.length];
        if (!candidate.isEliminated) {
            return candidate;
        }
    }
    throw new Error('No non-eliminated players in the room');
}
/**
 * Gets the team with the fewest players
 * If tied, returns the first team (sorted by teamId) among those with the minimum count
 */
export function getTeamWithFewestPlayers(state) {
    const teamIds = Object.keys(state.teams);
    if (teamIds.length === 0) {
        return undefined;
    }
    // Initialize player counts for all teams
    const playerCounts = Object.fromEntries(teamIds.map((id) => [id, state.teams[id].teamPlayerIds.length]));
    // Find the minimum player count
    const minCount = Math.min(...Object.values(playerCounts));
    // Find all teamIds with the minimum player count, sorted for deterministic selection
    const tiedTeamIds = teamIds
        .filter((id) => playerCounts[id] === minCount)
        .sort();
    // Select the first team (deterministic)
    const chosenId = tiedTeamIds[0];
    return state.teams[chosenId];
}
/**
 * Gets one or more game elements (turn, prompt, answer, vote) by type and id(s).
 * Always returns an array of found elements.
 */
export function getElementsByType(state, elementType, elementIds) {
    const ids = Array.isArray(elementIds) ? elementIds : [elementIds];
    switch (elementType) {
        case GameElementType.TURN:
            return ids.map((id) => state.turns[id]).filter(Boolean);
        case GameElementType.PROMPT:
            return ids.map((id) => state.prompts[id]).filter(Boolean);
        case GameElementType.ANSWER:
            return ids.map((id) => state.answers[id]).filter(Boolean);
        case GameElementType.VOTE:
            return ids.map((id) => state.votes[id]).filter(Boolean);
        default:
            return [];
    }
}
/**
 * Gets all elements in the current interaction window assigned to a specific player.
 * @param state The RoomState
 * @param playerId The playerId to filter by
 * @returns Array of elements assigned to the player in the current window
 */
export function getMyWindowAssignments(state, playerId) {
    const window = state.currentInteractionWindow;
    if (!window)
        return [];
    let collection;
    switch (window.elementType) {
        case GameElementType.TURN:
            collection = state.turns;
            break;
        case GameElementType.PROMPT:
            collection = state.prompts;
            break;
        case GameElementType.ANSWER:
            collection = state.answers;
            break;
        case GameElementType.VOTE:
            collection = state.votes;
            break;
        default:
            collection = undefined;
    }
    if (!collection)
        return [];
    // TypeScript now knows collection is defined after the early return
    const definedCollection = collection;
    return window.elementIds
        .map((id) => definedCollection[id])
        .filter((el) => !!el && el.assignedPlayerId === playerId);
}
/**
 * Check if no players participated in an interaction window
 * @param state - The room state
 * @returns true if no players participated (all elements in current window have empty content)
 */
export function checkNoParticipation(state) {
    // If there's no current interaction window, we can't determine participation
    if (!state.currentInteractionWindow) {
        return false;
    }
    const { elementType, elementIds } = state.currentInteractionWindow;
    // If there are no elements in the window, consider it as no participation
    if (!elementIds || elementIds.length === 0) {
        return true;
    }
    // Get all elements in the current window
    const elements = getElementsByType(state, elementType, elementIds);
    // Check if any element has non-empty content
    // If at least one element has content, then there was participation
    // If at least one element has content, then there was participation
    const hasAnyParticipation = elements.some(hasContent);
    // Return true if NO participation (all elements have empty content)
    return !hasAnyParticipation;
}
// Tree traversal utilities for game elements
/**
 * Gets all prompts for a given turn
 */
export function getPromptsForTurn(state, turnId) {
    const turn = state.turns[turnId];
    if (!turn || !Array.isArray(turn.promptIds))
        return [];
    return turn.promptIds.map((pid) => state.prompts[pid]).filter(Boolean);
}
/**
 * Gets all answers for a given turn
 */
export function getAnswersForTurn(state, turnId) {
    return getPromptsForTurn(state, turnId).flatMap((prompt) => (prompt.answerIds || []).map((aid) => state.answers[aid]).filter(Boolean));
}
/**
 * Gets all votes for a given turn
 */
export function getVotesForTurn(state, turnId) {
    return getAnswersForTurn(state, turnId).flatMap((answer) => (answer.voteIds || []).map((vid) => state.votes[vid]).filter(Boolean));
}
/**
 * Gets all answers for a given prompt
 */
export function getAnswersForPrompt(state, promptId) {
    const prompt = state.prompts[promptId];
    if (!prompt || !Array.isArray(prompt.answerIds))
        return [];
    return prompt.answerIds.map((aid) => state.answers[aid]).filter(Boolean);
}
/**
 * Gets all votes for a given prompt
 */
export function getVotesForPrompt(state, promptId) {
    return getAnswersForPrompt(state, promptId).flatMap((answer) => (answer.voteIds || []).map((vid) => state.votes[vid]).filter(Boolean));
}
/**
 * Gets all votes for a given answer
 */
export function getVotesForAnswer(state, answerId) {
    const answer = state.answers[answerId];
    if (!answer || !Array.isArray(answer.voteIds))
        return [];
    return answer.voteIds.map((vid) => state.votes[vid]).filter(Boolean);
}
/**
 * Gets the Prompt object for a given vote (vote → answer → prompt)
 */
export function getPromptForVote(state, voteId) {
    const vote = state.votes[voteId];
    if (!vote)
        return undefined;
    const answer = state.answers[vote.parentId];
    if (!answer)
        return undefined;
    return state.prompts[answer.parentId];
}
/**
 * Gets the Turn object for a given vote (vote → answer → prompt → turn)
 */
export function getTurnForVote(state, voteId) {
    const prompt = getPromptForVote(state, voteId);
    if (!prompt)
        return undefined;
    return state.turns[prompt.parentId];
}
/**
 * Gets the Turn object for a given answer (answer → prompt → turn)
 */
export function getTurnForAnswer(state, answerId) {
    const answer = state.answers[answerId];
    if (!answer)
        return undefined;
    const prompt = state.prompts[answer.parentId];
    if (!prompt)
        return undefined;
    return state.turns[prompt.parentId];
}
/**
 * Returns the latest turn object by sorting turn IDs (lexicographically, e.g. T001, T002, ...).
 */
export function getLatestTurn(state) {
    const turnIds = Object.keys(state.turns);
    if (turnIds.length === 0)
        return undefined;
    const latestTurnId = turnIds.sort().slice(-1)[0];
    return state.turns[latestTurnId];
}
/**
 * Returns the full tree for a turn: the turn, its prompts, answers, and votes as arrays.
 */
export function getTurnTree(state, turnId) {
    const turn = state.turns[turnId];
    if (!turn)
        return { turn: undefined, prompts: [], answers: [], votes: [] };
    const prompts = (turn.promptIds || [])
        .map((pid) => state.prompts[pid])
        .filter(Boolean);
    const answers = prompts.flatMap((prompt) => (prompt.answerIds || []).map((aid) => state.answers[aid]).filter(Boolean));
    const votes = answers.flatMap((answer) => (answer.voteIds || []).map((vid) => state.votes[vid]).filter(Boolean));
    return { turn, prompts, answers, votes };
}
/**
 * Gets the current turn number (1-based) based on total turns and remaining turns
 */
export function getCurrentTurnNumber(state) {
    const totalTurns = state.gameConfig.gameTotalTurns || 4;
    return totalTurns - (state.turnsRemaining || 0);
}
/**
 * Gets the previous turn in sequence
 */
export function getPreviousTurn(state, currentTurnId) {
    const turnIds = Object.keys(state.turns).sort();
    const currentIndex = turnIds.indexOf(currentTurnId);
    if (currentIndex <= 0)
        return undefined;
    const previousTurnId = turnIds[currentIndex - 1];
    return state.turns[previousTurnId];
}
/**
 * Checks if a turn had no participation by examining its answers
 */
export function checkPrevTurnParticipation(state, turnId) {
    const turn = state.turns[turnId];
    if (!turn)
        return false;
    const answers = getAnswersForTurn(state, turnId);
    if (answers.length === 0)
        return true;
    const hasAnyParticipation = answers.some(hasContent);
    return !hasAnyParticipation;
}
/**
 * Gets all players as an array
 */
export function getPlayers(state) {
    return Object.values(state.players).filter((player) => player.role === SenderRoleType.PLAYER);
}
/**
 * Gets all player IDs as an array
 */
export function getPlayerIds(state) {
    return Object.keys(state.players);
}
/**
 * Gets all non-eliminated players
 */
export function getPlayersNotEliminated(state) {
    return getPlayers(state).filter((player) => !player.isEliminated);
}
/**
 * Gets all eliminated players
 */
export function getPlayersEliminated(state) {
    return getPlayers(state).filter((player) => player.isEliminated);
}
/**
 * Gets players eligible for elimination (not eliminated and scoreTurn === 0)
 * Used in Ego Maniacs game
 */
export function getPlayersEligibleForElimination(state) {
    return getPlayers(state).filter((player) => !player.isEliminated && player.scoreTurn === 0);
}
/**
 * Gets players with the highest scoreTurn
 */
export function getPlayersMaxScoreTurn(state) {
    const players = getPlayers(state);
    if (players.length === 0)
        return [];
    const maxScore = Math.max(...players.map((p) => p.scoreTurn));
    return players.filter((p) => p.scoreTurn === maxScore);
}
/**
 * Gets players with the highest scoreGame
 */
export function getPlayersMaxScoreGame(state) {
    const players = getPlayers(state);
    if (players.length === 0)
        return [];
    const maxScore = Math.max(...players.map((p) => p.scoreGame));
    return players.filter((p) => p.scoreGame === maxScore);
}
/**
 * Returns the latest turn and partitions prompts, answers, and votes into mine/others for the given playerId.
 * Also returns all prompts, answers, and votes for the latest turn.
 * Naming: promptsAll, promptsMine, promptsOthers, etc.
 * Only requires turns, prompts, answers, votes, and myPlayerId.
 */
export function getLatestTurnAssignments(turns, prompts, answers, votes, myPlayerId) {
    const turnIds = Object.keys(turns);
    if (turnIds.length === 0) {
        return {
            turn: undefined,
            promptsMine: [],
            promptsOthers: [],
            promptsAll: [],
            answersMine: [],
            answersOthers: [],
            answersAll: [],
            votesMine: [],
            votesOthers: [],
            votesAll: [],
        };
    }
    const latestTurnId = turnIds.sort().slice(-1)[0];
    const turn = turns[latestTurnId];
    if (!turn) {
        return {
            turn: undefined,
            promptsMine: [],
            promptsOthers: [],
            promptsAll: [],
            answersMine: [],
            answersOthers: [],
            answersAll: [],
            votesMine: [],
            votesOthers: [],
            votesAll: [],
        };
    }
    const promptsArr = (turn.promptIds || [])
        .map((pid) => prompts[pid])
        .filter(Boolean);
    const answersArr = promptsArr.flatMap((prompt) => (prompt.answerIds || []).map((aid) => answers[aid]).filter(Boolean));
    const votesArr = answersArr.flatMap((answer) => (answer.voteIds || []).map((vid) => votes[vid]).filter(Boolean));
    const promptsMine = promptsArr.filter((p) => p.assignedPlayerId === myPlayerId);
    const promptsOthers = promptsArr.filter((p) => p.assignedPlayerId && p.assignedPlayerId !== myPlayerId);
    const answersMine = answersArr.filter((a) => a.assignedPlayerId === myPlayerId);
    const answersOthers = answersArr.filter((a) => a.assignedPlayerId && a.assignedPlayerId !== myPlayerId);
    const votesMine = votesArr.filter((v) => v.assignedPlayerId === myPlayerId);
    const votesOthers = votesArr.filter((v) => v.assignedPlayerId && v.assignedPlayerId !== myPlayerId);
    return {
        turn,
        promptsMine,
        promptsOthers,
        promptsAll: promptsArr,
        answersMine,
        answersOthers,
        answersAll: answersArr,
        votesMine,
        votesOthers,
        votesAll: votesArr,
    };
}
//# sourceMappingURL=roomState.js.map